import React, { useCallback, useEffect, useRef, useState } from "react";
import orendaService from "../../services/orenda.service";
import { parsePassKeySessionResponse } from "./orendaApplicationHelpers";
import { promptPasskeyAssertion, describePasskeyError } from "./orendaWebAuthn";
import OrendaSendMoney from "./OrendaSendMoney";

// Beneficiaries screen — lives inside OrendaDashboard's view-switcher as a
// sibling of the main dashboard pane. Three sub-flows:
//
//   • idle            → list + "Add beneficiary" button
//   • add-form        → the add form (name + account type + bank fields)
//   • waiting-passkey → confirming with Windows Hello / Touch ID
//   • submitting      → POST in flight
//   • confirm-delete  → confirm overlay for a specific beneficiary row
//
// Create requires an Orenda step-up (passkey/totp/pin). We reuse the same
// passkey challenge the card-reveal flow uses — one less thing to teach
// the user, one less thing to maintain.

// ─── parsers ──────────────────────────────────────────────────────

// Unwrap Bopayments { payload: { data: [...] | {...}, errorMessage } }.
// The backend's typed path often drops nested arrays, so prefer the raw
// errorMessage body when present; it carries the verbatim Orenda response.
const parseBeneficiariesListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  const data =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(typed?.data) && typed.data) ||
    (Array.isArray(typed) && typed) ||
    [];
  const nextToken = raw?.nextToken ?? typed?.nextToken ?? null;
  return { data, nextToken };
};

const parseSingleBeneficiaryResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  return raw?.data ?? typed?.data ?? null;
};

// ─── helpers ──────────────────────────────────────────────────────

const formatAccountLine = (b) => {
  const t = String(b?.account?.type || b?.type || "").toLowerCase();
  if (t === "iban" && b.account?.iban) return b.account.iban;
  if ((b.account?.sortCode || b.sortCode) && (b.account?.accountNumber || b.accountNumber)) {
    const sc = b.account?.sortCode || b.sortCode;
    const an = b.account?.accountNumber || b.accountNumber;
    return `${sc} · ${an}`;
  }
  return b.account?.iban || b.accountNumber || "—";
};

const initials = (name) => {
  if (!name) return "·";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
};

// ─── component ────────────────────────────────────────────────────

const OrendaBeneficiaries = () => {
  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [listError, setListError] = useState(null);
  const [list, setList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Pre-selected beneficiary for the inline "Pay" button on each row.
  // When set, OrendaSendMoney mounts with the picker skipped.
  const [payTarget, setPayTarget] = useState(null);
  const refetchRef = useRef(null);

  const loadList = useCallback(async () => {
    setListError(null);
    setPhase("loading");
    try {
      const resp = await orendaService.listBeneficiariesOrenda({ limit: 50 });
      const parsed = parseBeneficiariesListResponse(resp);
      setList(parsed.data);
      setPhase("ready");
    } catch (err) {
      setListError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't load beneficiaries."
      );
      setPhase("error");
    }
  }, []);
  refetchRef.current = loadList;

  useEffect(() => { loadList(); }, [loadList]);

  const runDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await orendaService.deleteBeneficiaryOrenda({ beneficiaryId: confirmDelete.id });
      setDeleting(false);
      setConfirmDelete(null);
      await loadList();
    } catch (err) {
      setDeleting(false);
      setDeleteError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "The deletion couldn't complete."
      );
    }
  };

  return (
    <section className="orenda-benes">
      <header className="orenda-benes-head">
        <div>
          <div className="orenda-eyebrow">Beneficiaries</div>
          <h1 className="orenda-benes-title">People you pay</h1>
          <p className="orenda-benes-sub">
            Save payees here so you can send money without re-entering their
            bank details every time.
          </p>
        </div>
        <button
          type="button"
          className="orenda-hero-cta"
          onClick={() => setAdding(true)}
        >
          <PlusIcon />
          Add beneficiary
        </button>
      </header>

      {phase === "loading" && (
        <div className="orenda-benes-card orenda-benes-empty">
          Loading…
        </div>
      )}

      {phase === "error" && (
        <div className="orenda-benes-card orenda-benes-empty">
          <p>{listError}</p>
          <button
            type="button"
            className="orenda-btn-primary"
            onClick={loadList}
          >
            Try again
          </button>
        </div>
      )}

      {phase === "ready" && list.length === 0 && (
        <div className="orenda-benes-card orenda-benes-empty">
          <h3>No beneficiaries yet</h3>
          <p>
            Add your first payee and they'll show up here. Their bank
            details are stored securely and used only for the payments you
            initiate.
          </p>
          <button
            type="button"
            className="orenda-hero-cta"
            onClick={() => setAdding(true)}
          >
            <PlusIcon />
            Add beneficiary
          </button>
        </div>
      )}

      {phase === "ready" && list.length > 0 && (
        <ul className="orenda-benes-list">
          {list.map((b) => (
            <li key={b.id} className="orenda-benes-row">
              <div className="orenda-benes-row-avatar" aria-hidden>
                {initials(b.name)}
              </div>
              <div className="orenda-benes-row-main">
                <div className="orenda-benes-row-name">{b.name || "—"}</div>
                <div className="orenda-benes-row-account">
                  {formatAccountLine(b)}
                  {b.currency && (
                    <span className="orenda-benes-row-chip">{b.currency}</span>
                  )}
                </div>
              </div>
              <div className="orenda-benes-row-actions">
                <button
                  type="button"
                  className="orenda-btn-primary orenda-benes-row-pay"
                  onClick={() => setPayTarget(b)}
                  aria-label={`Pay ${b.name}`}
                >
                  <PayIcon />
                  Pay
                </button>
                <button
                  type="button"
                  className="orenda-benes-row-delete"
                  onClick={() => {
                    setDeleteError(null);
                    setConfirmDelete({ id: b.id, name: b.name });
                  }}
                  aria-label={`Remove ${b.name}`}
                >
                  <TrashIcon />
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {payTarget && (
        <OrendaSendMoney
          presetBeneficiary={payTarget}
          onClose={() => setPayTarget(null)}
          onSent={() => setPayTarget(null)}
        />
      )}

      {adding && (
        <AddBeneficiaryModal
          onClose={() => setAdding(false)}
          onCreated={async () => {
            setAdding(false);
            await loadList();
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          name={confirmDelete.name}
          busy={deleting}
          error={deleteError}
          onCancel={() => {
            if (deleting) return;
            setConfirmDelete(null);
            setDeleteError(null);
          }}
          onConfirm={runDelete}
        />
      )}
    </section>
  );
};

// ─── Add beneficiary ──────────────────────────────────────────────

const AddBeneficiaryModal = ({ onClose, onCreated }) => {
  const [kind, setKind] = useState("iban"); // iban | uk
  const [name, setName] = useState("");
  const [iban, setIban] = useState("");
  const [bic, setBic] = useState("");
  const [bankName, setBankName] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [isCorporate, setIsCorporate] = useState(false);
  const [phase, setPhase] = useState("form"); // form | passkey | submitting | done
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && phase === "form") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, phase]);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Required.";
    if (kind === "iban") {
      if (!iban.trim()) errs.iban = "Required.";
      else if (iban.replace(/\s+/g, "").length < 15) errs.iban = "That doesn't look like a full IBAN.";
    } else {
      if (!/^\d{6}$/.test(sortCode.replace(/[\s-]/g, "")))
        errs.sortCode = "6 digits (e.g. 00-00-00).";
      if (!/^\d{8}$/.test(accountNumber.replace(/\s+/g, "")))
        errs.accountNumber = "8 digits.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setError(null);

    // Fetch the passkey challenge; prompt Windows Hello / Touch ID; carry
    // the resulting (passkeySession, assertion) into the create call.
    //
    // Use the GENERIC step-up endpoint — NOT getPassKeySessionOrenda, which
    // chains the card /secure-key call and 403s with
    // GET_CUSTOMER_SECURE_KEY_FAILED for non-card flows.
    setPhase("passkey");
    let passkeySession, assertion;
    try {
      const sessionResp = await orendaService.getStepUpPassKeySessionOrenda();
      const session = parsePassKeySessionResponse(sessionResp);
      if (!session.passkeySession || !session.fido2options) {
        throw new Error(
          "Couldn't prepare a secure confirmation. Please try again."
        );
      }
      passkeySession = session.passkeySession;
      assertion = await promptPasskeyAssertion(session.fido2options);
    } catch (err) {
      setError(describePasskeyError(err));
      setPhase("form");
      return;
    }

    setPhase("submitting");
    const account =
      kind === "iban"
        ? {
            type: "iban",
            iban: iban.replace(/\s+/g, "").toUpperCase(),
            ...(bic.trim() ? { bic: bic.trim().toUpperCase() } : {}),
            ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
          }
        : {
            type: "uk",
            sortCode: sortCode.replace(/[\s-]/g, ""),
            accountNumber: accountNumber.replace(/\s+/g, ""),
          };

    try {
      await orendaService.createBeneficiaryOrenda({
        name: name.trim(),
        account,
        confirmation: {
          method: "passkey",
          passkeySession,
          assertion,
        },
        isCorporate,
      });
      setPhase("done");
      // Brief success flash then close.
      window.setTimeout(() => onCreated?.(), 700);
    } catch (err) {
      setError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't save that beneficiary. Please try again."
      );
      setPhase("form");
    }
  };

  return (
    <div
      className="orenda-reveal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="orenda-benes-add-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && phase === "form") onClose?.();
      }}
    >
      <div className="orenda-reveal orenda-benes-form-card">
        <div className="orenda-reveal-head">
          <div>
            <div className="orenda-reveal-eyebrow">New beneficiary</div>
            <h2 id="orenda-benes-add-title" className="orenda-reveal-title">
              Add a payee
            </h2>
          </div>
          <button
            type="button"
            className="orenda-reveal-close"
            onClick={onClose}
            disabled={phase === "submitting" || phase === "passkey"}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="orenda-reveal-body">
          {(phase === "form" || phase === "submitting") && (
            <form
              className="orenda-benes-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (phase === "form") submit();
              }}
            >
              <div className="orenda-benes-field">
                <label htmlFor="bene-name">Beneficiary name</label>
                <input
                  id="bene-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 100))}
                  placeholder="Jane Smith"
                  autoFocus
                  disabled={phase !== "form"}
                />
                {fieldErrors.name && (
                  <div className="orenda-benes-field-err">{fieldErrors.name}</div>
                )}
              </div>

              <div className="orenda-benes-field">
                <label>Account type</label>
                <div className="orenda-benes-segmented">
                  <button
                    type="button"
                    className={kind === "iban" ? "is-active" : ""}
                    onClick={() => setKind("iban")}
                    disabled={phase !== "form"}
                  >
                    IBAN
                  </button>
                  <button
                    type="button"
                    className={kind === "uk" ? "is-active" : ""}
                    onClick={() => setKind("uk")}
                    disabled={phase !== "form"}
                  >
                    UK sort code
                  </button>
                </div>
              </div>

              {kind === "iban" && (
                <>
                  <div className="orenda-benes-field">
                    <label htmlFor="bene-iban">IBAN</label>
                    <input
                      id="bene-iban"
                      type="text"
                      value={iban}
                      onChange={(e) => setIban(e.target.value.slice(0, 40))}
                      placeholder="GB29 NWBK 6016 1331 9268 19"
                      spellCheck={false}
                      autoCapitalize="characters"
                      disabled={phase !== "form"}
                    />
                    {fieldErrors.iban && (
                      <div className="orenda-benes-field-err">{fieldErrors.iban}</div>
                    )}
                  </div>
                  <div className="orenda-reveal-grid">
                    <div className="orenda-benes-field">
                      <label htmlFor="bene-bic">BIC / SWIFT (optional)</label>
                      <input
                        id="bene-bic"
                        type="text"
                        value={bic}
                        onChange={(e) => setBic(e.target.value.slice(0, 11))}
                        placeholder="NWBKGB2L"
                        disabled={phase !== "form"}
                      />
                    </div>
                    <div className="orenda-benes-field">
                      <label htmlFor="bene-bank">Bank name (optional)</label>
                      <input
                        id="bene-bank"
                        type="text"
                        value={bankName}
                        onChange={(e) => setBankName(e.target.value.slice(0, 60))}
                        placeholder="NatWest"
                        disabled={phase !== "form"}
                      />
                    </div>
                  </div>
                </>
              )}

              {kind === "uk" && (
                <div className="orenda-reveal-grid">
                  <div className="orenda-benes-field">
                    <label htmlFor="bene-sortcode">Sort code</label>
                    <input
                      id="bene-sortcode"
                      type="text"
                      value={sortCode}
                      onChange={(e) => setSortCode(e.target.value.slice(0, 10))}
                      placeholder="00-00-00"
                      inputMode="numeric"
                      disabled={phase !== "form"}
                    />
                    {fieldErrors.sortCode && (
                      <div className="orenda-benes-field-err">{fieldErrors.sortCode}</div>
                    )}
                  </div>
                  <div className="orenda-benes-field">
                    <label htmlFor="bene-acctnum">Account number</label>
                    <input
                      id="bene-acctnum"
                      type="text"
                      value={accountNumber}
                      onChange={(e) =>
                        setAccountNumber(e.target.value.replace(/\s+/g, "").slice(0, 8))
                      }
                      placeholder="12345678"
                      inputMode="numeric"
                      disabled={phase !== "form"}
                    />
                    {fieldErrors.accountNumber && (
                      <div className="orenda-benes-field-err">
                        {fieldErrors.accountNumber}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="orenda-benes-field">
                <label className="orenda-benes-checkbox">
                  <input
                    type="checkbox"
                    checked={isCorporate}
                    onChange={(e) => setIsCorporate(e.target.checked)}
                    disabled={phase !== "form"}
                  />
                  <span>This is a business account</span>
                </label>
              </div>

              {error && (
                <div
                  className="orenda-reveal-countdown"
                  style={{ color: "#c04040" }}
                >
                  {error}
                </div>
              )}

              <div className="orenda-benes-form-actions">
                <button
                  type="button"
                  className="orenda-btn-ghost"
                  onClick={onClose}
                  disabled={phase !== "form"}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="orenda-btn-primary"
                  disabled={phase !== "form"}
                >
                  {phase === "submitting" ? "Saving…" : "Save beneficiary"}
                </button>
              </div>
            </form>
          )}

          {phase === "passkey" && (
            <Spinner label="Confirm with your passkey to continue…" />
          )}

          {phase === "done" && (
            <div className="orenda-reveal-expired" style={{ padding: "28px 10px" }}>
              <div
                aria-hidden
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#e8f3eb",
                  color: "#2f6b3a",
                  margin: "0 auto 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                }}
              >
                ✓
              </div>
              <h3 className="orenda-reveal-expired-title">Beneficiary saved</h3>
              <p className="orenda-reveal-expired-sub">
                You'll see them in your list in a moment.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Confirm delete ───────────────────────────────────────────────

const ConfirmDeleteModal = ({ name, busy, error, onCancel, onConfirm }) => (
  <div
    className="orenda-reveal-backdrop"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="orenda-benes-del-title"
    onClick={(e) => {
      if (e.target === e.currentTarget && !busy) onCancel?.();
    }}
  >
    <div className="orenda-reveal" style={{ maxWidth: 440 }}>
      <div className="orenda-reveal-head">
        <div>
          <div className="orenda-reveal-eyebrow">Remove beneficiary</div>
          <h2 id="orenda-benes-del-title" className="orenda-reveal-title">
            Remove {name || "this payee"}?
          </h2>
        </div>
      </div>
      <div className="orenda-reveal-body">
        <p className="orenda-reveal-expired-sub">
          You can always add them back later. Any scheduled payments to this
          beneficiary will stop.
        </p>
        {error && (
          <div
            className="orenda-reveal-countdown"
            style={{ color: "#c04040" }}
          >
            {error}
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="orenda-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="orenda-btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Removing…" : "Yes, remove"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── atoms ────────────────────────────────────────────────────────

const Spinner = ({ label }) => (
  <div className="orenda-reveal-spinner">
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-dot" />
    <div className="orenda-reveal-spinner-label">{label}</div>
  </div>
);

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const PayIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

export default OrendaBeneficiaries;
